import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@/core/api/apiTypes";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { profileApiService } from "../services/profileApiService";
import { fromProfileDtoToProfile, type Profile } from "../types/profileTypes";

const staleTime = 30 * 60 * 1000;

export const profileKeys = {
  all: ["profile"] as const,
  me: (userId?: string) => [...profileKeys.all, "me", userId] as const,
};

export const useProfile = () => {
  const { user, loading, getAuthGeneration } = useAuth();
  return useQuery<Profile | null, Error>({
    queryKey: profileKeys.me(user?.id),
    queryFn: async ({ signal }) => {
      const generation = getAuthGeneration();
      try {
        const dto = await profileApiService.getMe(signal);
        signal.throwIfAborted();
        if (generation !== getAuthGeneration()) throw new Error("Your account changed.");
        const profile = fromProfileDtoToProfile(dto);
        if (profile.userId !== user?.id) throw new Error("The profile belongs to a different account.");
        return profile;
      } catch (error) {
        signal.throwIfAborted();
        if (generation !== getAuthGeneration()) throw new Error("Your account changed.");
        if (error instanceof ApiError && error.Status === 404) return null;
        throw error;
      }
    },
    staleTime,
    enabled: !!user && !loading,
  });
};
